import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	getAllLinks,
	getChannelIdFromSlackLink,
	isMailtoLink,
	isSlackLink,
} from './utils/links.ts';

export const useDetectPastedThirdPartyLinks = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId, orgId } = useSessionData();

	return (content: ADFEntity, startingLinks?: Set<string>) => {
		if (startingLinks === undefined) {
			return;
		}

		const pastedSlackLinks: string[] = [];
		const pastedMailtoLinks: string[] = [];

		for (const link of getAllLinks(content)) {
			if (startingLinks.has(link)) {
				continue;
			}
			if (isSlackLink(link)) {
				pastedSlackLinks.push(link);
			} else if (isMailtoLink(link)) {
				pastedMailtoLinks.push(link);
			}
		}

		// 3P in 1P exp eligibility check flow
		if (
			(pastedSlackLinks.length === 0 && pastedMailtoLinks.length === 0) ||
			!fg('twcg-853-3p-in-1p-aa-test-gate')
		) {
			return;
		}

		// Check cohort, fire exposure and early return for control
		if (!expValEquals('twcg-853-3p-in-1p-aa-test', 'isEnabled', true)) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'pasted',
				actionSubject: '3PLink',
				actionSubjectId: 'editorPaste',
				source: 'inviteOnLinkInsert',
				attributes: {
					slackLinks: pastedSlackLinks,
					channelIds: pastedSlackLinks.map(getChannelIdFromSlackLink),
					mailtoLinkCount: pastedMailtoLinks.length,
					orgId,
					accountId: userId,
				},
			},
		}).fire();
	};
};
